import { useTranslation } from 'react-i18next';
import { Box, Checkbox, IconButton, MenuItem, Select, Stack, styled, Text } from 'shell/components';

const QuestionCriterionSelect = ({
    questionId,
    questionNumber,
    criteria,
    selectedIds,
    mappedCriterionIds,
    onChange,
    disabled = false,
}) => {
    const { t } = useTranslation(['quiz', 'common']);
    const selectedCriteria = criteria.filter(({ id }) => selectedIds.includes(id));
    const labelId = `quiz-question-${questionId}-criteria-label`;

    return (
        <Stack spacing={5}>
            <CriteriaSelect
                displayEmpty
                label={t('create.criteria-label', 'Criterion')}
                labelId={labelId}
                multiple
                required
                disabled={disabled}
                value={selectedIds}
                renderValue={(selected) =>
                    selected.length ? (
                        <SelectionCount
                            aria-label={t('create.criteria-selected', '{{count}} criteria selected', {
                                count: selected.length,
                            })}
                        >
                            {selected.length}
                        </SelectionCount>
                    ) : (
                        <SelectPlaceholder color={(theme) => theme.palette.ensightful.neutral[500]}>
                            {t('create.select-criteria', 'Select a criterion')}
                        </SelectPlaceholder>
                    )
                }
                onChange={({ target: { value } }) => onChange(typeof value === 'string' ? value.split(',') : value)}
            >
                {criteria.map((criterion) => {
                    const isSelected = selectedIds.includes(criterion.id);
                    const isMappedElsewhere = mappedCriterionIds.has(criterion.id) && !isSelected;
                    return (
                        <MenuItem key={criterion.id} value={criterion.id} disabled={isMappedElsewhere}>
                            <Checkbox checked={isSelected} disabled={isMappedElsewhere} />
                            <Text>{criterion.title}</Text>
                        </MenuItem>
                    );
                })}
            </CriteriaSelect>
            {selectedCriteria.length > 0 && (
                <Stack spacing={2}>
                    <Text weight={'medium'}>
                        {t(
                            'create.selected-criteria',
                            selectedCriteria.length === 1
                                ? 'Criterion for this question:'
                                : 'Criteria for this question:',
                            { count: selectedCriteria.length }
                        )}
                    </Text>
                    <Stack spacing={2}>
                        {selectedCriteria.map((criterion) => (
                            <SelectedCriterion
                                key={criterion.id}
                                direction={'row'}
                                alignItems={'center'}
                                justifyContent={'space-between'}
                                spacing={3}
                            >
                                <Text color={'light'}>{criterion.title}</Text>
                                <IconButton
                                    kind={'delete'}
                                    size={'small'}
                                    variant={'square'}
                                    disabled={disabled}
                                    aria-label={t(
                                        'create.remove-criterion',
                                        'Remove {{criterion}} from question {{number}}',
                                        { criterion: criterion.title, number: questionNumber }
                                    )}
                                    tooltip={{ component: t('common:controls.delete', 'Delete') }}
                                    onClick={() => onChange(selectedIds.filter((id) => id !== criterion.id))}
                                />
                            </SelectedCriterion>
                        ))}
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
};

const CriteriaSelect = styled(Select)(({ theme }) => ({
    width: 'min(100%, 414px)',
    '& > .MuiInputBase-root': {
        width: '100%',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${theme.palette.ensightful.neutral[500]}`,
    },
    '& > .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.ensightful.primary[700],
    },
    '& > .Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${theme.palette.ensightful.primary[500]} !important`,
    },
    '& .MuiSelect-select': {
        minWidth: theme.spacing(25),
    },
}));

const SelectPlaceholder = styled(Text)(() => ({ marginTop: '2px' }));
const SelectionCount = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: theme.spacing(8),
    height: theme.spacing(6),
    borderRadius: '8px',
    background: theme.palette.ensightful.primary[100],
    color: theme.palette.ensightful.neutral[900],
}));
const SelectedCriterion = styled(Stack)(() => ({ minWidth: 0 }));

export default QuestionCriterionSelect;
